import { Col } from 'antd';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import { EmailProvider } from '../../../../api/types';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import PasswordField from '../../../../components/fields/PasswordField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../utilities/hasPermissions';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';

const CompanyEmailFields = () => {
    const { t } = useTranslation(['companyDetails']);
    const { emailProviderOptions } = useSystemOptions();
    const { yesNoSwitch } = useSystemSwitchData();

    const [{ value: emailSettings }] = useField('emailSettings');
    const { permissions: accountPermissions } = useAccount();
    const company = useCompany(true);

    const hasPermission = company
        ? hasPermissions(company.permissions, [permissionKind.updateCompany]) &&
          hasPermissions(accountPermissions, [permissionKind.updateCompany])
        : hasPermissions(accountPermissions, [permissionKind.updateCompany]);

    return (
        <>
            <Col lg={8} xs={24}>
                <FormFields.SelectField
                    {...t('companyDetails:fields.emailProvider', { returnObjects: true })}
                    disabled={!hasPermission}
                    name="emailSettings.provider"
                    options={emailProviderOptions}
                    required
                    showSearch
                />
            </Col>
            {emailSettings.provider === EmailProvider.Smtp && (
                <>
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            {...t('companyDetails:fields.emailHost', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="emailSettings.host"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.InputNumberField
                            {...t('companyDetails:fields.emailPort', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="emailSettings.port"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.SwitchField
                            {...t('companyDetails:fields.emailSsl', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="emailSettings.isSslEnabled"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.SwitchField
                            {...t('companyDetails:fields.emailAuthenticationRequired', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="emailSettings.isAuthenticationRequired"
                            {...yesNoSwitch}
                        />
                    </Col>
                    {emailSettings.isAuthenticationRequired && (
                        <>
                            <Col lg={8} xs={24}>
                                <FormFields.InputField
                                    {...t('companyDetails:fields.emailUsername', { returnObjects: true })}
                                    disabled={!hasPermission}
                                    name="emailSettings.user"
                                    required
                                />
                            </Col>
                            <Col lg={8} xs={24}>
                                <PasswordField
                                    {...t('companyDetails:fields.emailPassword', { returnObjects: true })}
                                    disabled={!hasPermission}
                                    name="emailSettings.password"
                                    required
                                />
                            </Col>
                        </>
                    )}
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            {...t('companyDetails:fields.emailFrom', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="emailSettings.from"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <SingleSecretUploadField
                            {...t('companyDetails:fields.emailCertificate', { returnObjects: true })}
                            name="emailSettings.certificate"
                        />
                    </Col>
                </>
            )}
        </>
    );
};

export default CompanyEmailFields;
